import { useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Combobox,
  Divider,
  FormField,
  Heading,
  Label,
  Modal,
  Paragraph,
  Tag,
  TextButton,
} from '@zillow/constellation-10';
import { IconCameraExposureFilled } from '@zillow/constellation-icons';

import { useAutoSpotlight } from './AutoSpotlightContext';
import { SPOTLIGHT_COPY as COPY } from './spotlightCopy';

export function SpotlightReassignModal() {
  const ctx = useAutoSpotlight();
  const [targetAlias, setTargetAlias] = useState('');

  const open = Boolean(ctx?.reassignCandidate);
  const occupants = ctx?.reassignOccupants ?? [];
  // Occupants are still unknown during the initial request or a retry without cached data.
  const showPicker = ctx?.isReassignOccupantsLoading || occupants.length > 0;

  if (!ctx) {
    return null;
  }

  // Default to the first occupant during render; targetAlias only holds the user's explicit pick,
  // and is reset on close so each time the dialog reopens it defaults fresh (no prop-sync effect).
  const selectedAlias = targetAlias || occupants[0]?.alias || '';
  const options = occupants.map((occupant) => ({
    label: occupant.label,
    meta: `${occupant.meta}${occupant.meta ? ' · ' : ''}Pinned`,
    value: occupant.alias,
  }));

  const handleChange = (value: string | Array<string>) => {
    setTargetAlias(Array.isArray(value) ? (value[0] ?? '') : value);
  };

  const handleCancel = () => {
    setTargetAlias('');
    ctx.cancelReassign();
  };

  const handleConfirm = () => {
    setTargetAlias('');
    ctx.confirmReassign(selectedAlias);
  };

  return (
    <Modal.Root open={open} onOpenChange={(next) => !next && handleCancel()} size={{ base: 'fullScreen', md: 'md' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-panda-theme="zillow">
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                {COPY.reassignModal.title}
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '400',
                  alignItems: 'flex-start',
                }}
              >
                {showPicker ? (
                  <>
                    <Tag size="md" tone="yellow" icon={<IconCameraExposureFilled />}>
                      All {ctx.licenses} licenses pinned
                    </Tag>
                    <Paragraph>
                      {COPY.reassignModal.description.replace('{candidate}', ctx.reassignCandidateLabel)}
                    </Paragraph>
                    <FormField
                      label={<Label required>{COPY.reassignModal.comboboxLabel}</Label>}
                      required
                      css={{ alignSelf: 'stretch' }}
                      control={
                        <Combobox
                          value={selectedAlias}
                          onChange={handleChange}
                          showLabelForValue
                          options={options}
                          placeholder={COPY.reassignModal.comboboxPlaceholder}
                          size="md"
                        />
                      }
                    />
                  </>
                ) : (
                  <Paragraph>{COPY.reassignModal.empty}</Paragraph>
                )}
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Actions">
                <TextButton tone="brand" onClick={handleCancel}>
                  {COPY.reassignModal.cancel}
                </TextButton>
                {showPicker && (
                  <Button tone="brand" emphasis="filled" size="md" disabled={!selectedAlias} onClick={handleConfirm}>
                    {COPY.reassignModal.confirm}
                  </Button>
                )}
              </ButtonGroup>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}

export default SpotlightReassignModal;
